import { dailyBurn } from "./body";
import { deficitRule, proteinRule, type DeviceAnswers } from "./eating";
import { curWeight } from "./energy";
import type { FoodEntry, FoodItem, KayaState, MealSlot, MealTemplate, PlanItem } from "./types";

/**
 * What was eaten, and how it sits against what was burned.
 *
 * Kaya already knew the "out" side better than most trackers: `dailyBurn` adds resting burn, a
 * flat 20% for incidental movement, and the work it actually *measured*, instead of multiplying
 * BMR by a guessed activity factor. That matters here — the usual advice "don't eat back your
 * exercise calories" exists because a guessed factor already contains them. Kaya's does not, so
 * the measured work genuinely is extra and can be shown as such. (ADR-025)
 */

/** A teaspoon of oil or ghee. The research names invisible fat as the largest single error in
 *  Indian home food — a tablespoon is ~120 kcal, and it does not show on the plate. */
export const OIL_KCAL_PER_TSP = 45;
export const OIL_FAT_G_PER_TSP = 5;

/** The adult defaults; age and the questionnaire's answers adjust them in `eating.ts`. */
export { DEFAULT_DEFICIT_KCAL, DEFAULT_PROTEIN_PER_KG } from "./eating";
/** Past this the deficit stops being a diet. Flagged, not forbidden — it is the user's body. */
export const MAX_SAFE_DEFICIT_FRACTION = 0.35;

/**
 * The share of measured work the food budget credits. (ADR-041)
 *
 * People eat back part of what exercise burns without noticing (Careau et al. 2021 put the
 * compensation near 28 %), and MET-based estimates run high, so crediting all of Work made every
 * training day's budget the most generous and least accurate one of the week. Out and Work stay
 * whole on Energy (ADR-029) — only what eating is planned against is discounted.
 */
export const WORK_CREDIT = 0.7;

/** The part of Work the budget holds back. */
export function workHeldBack(workKcal: number): number {
  return Math.round(Math.max(0, workKcal) * (1 - WORK_CREDIT));
}

/** The day's calorie budget: Out, less the part of Work not credited, less the deficit. The one
 *  formula — `energyBalance`, `dayLedger` and `dailyTargets` all come through here. */
export function foodBudget(outKcal: number, workKcal: number, deficitKcal: number): number {
  return Math.max(0, outKcal - workHeldBack(workKcal) - deficitKcal);
}

export const MEALS: MealSlot[] = ["breakfast", "lunch", "dinner", "snack"];
export const MEAL_LABEL: Record<MealSlot, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks",
};

/** On unless switched off, like the treadmill — it is the feature this was all built for, and a
 *  toggle that starts off is a feature nobody finds. (ADR-022 set the precedent) */
export function foodOn(S: KayaState): boolean {
  return S.profile.foodOn !== false;
}

/** A tablespoon is three teaspoons — the one conversion between household units that holds for
 *  every food, because it is a volume measured with the same spoon. */
const SPOON_RATIO = 3;

/**
 * The household units a row can be counted in, its own first: a row measured in teaspoons can
 * also be counted in tablespoons and the other way round, because oil and ghee go into a pot by
 * the tablespoon and onto a roti by the teaspoon (ADR-048). Grams are always available and are
 * not listed here.
 */
export function householdUnits(item: FoodItem): { unit: string; g: number }[] {
  const own = { unit: item.u, g: item.g };
  const u = item.u.toLowerCase();
  if (u === "teaspoon") return [own, { unit: "tablespoon", g: Math.round(item.g * SPOON_RATIO * 10) / 10 }];
  if (u === "tablespoon") return [own, { unit: "teaspoon", g: Math.round((item.g / SPOON_RATIO) * 10) / 10 }];
  return [own];
}

/** Grams for `qty` of a unit: grams as they are, a spoon through `householdUnits`, and anything
 *  else as the food's own serving — an entry scaled or copied keeps whatever unit it was logged in. */
export function portionGrams(item: FoodItem, qty: number, unit: string): number {
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  if (unit === "g") return qty;
  const alt = householdUnits(item).find((h) => h.unit === unit);
  return qty * (alt ? alt.g : item.g);
}

/** Builds the entry from a table row, copying the values in — see `FoodEntry.kcal`. */
export function entryFor(
  item: FoodItem,
  opts: { qty: number; unit: string; meal: MealSlot; oilTsp?: number; at?: number; id?: string }
): FoodEntry {
  const grams = portionGrams(item, opts.qty, opts.unit);
  const scale = grams / 100;
  const oil = Math.max(0, opts.oilTsp || 0);
  const at = opts.at ?? Date.now();
  return {
    id: opts.id ?? `${at.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    code: item.i,
    name: item.n,
    at,
    meal: opts.meal,
    qty: opts.qty,
    unit: opts.unit,
    grams: Math.round(grams),
    kcal: Math.round(item.k * scale + oil * OIL_KCAL_PER_TSP),
    p: round1(item.p * scale),
    c: round1(item.c * scale),
    f: round1(item.f * scale + oil * OIL_FAT_G_PER_TSP),
    // absent when the row does not give it — a label may not declare fibre (invariant 18)
    ...(typeof item.fb === "number" ? { fb: round1(item.fb * scale) } : {}),
    ...(oil > 0 ? { oilTsp: oil } : {}),
    ...microsFor(item, scale),
  };
}

/** The five micronutrients for a portion, only those the row actually carries. Oil adds none of
 *  the minerals, and its saturated fat depends on which oil, so it is not guessed at. (ADR-026) */
function microsFor(item: FoodItem, scale: number): Pick<FoodEntry, MicroKey> {
  const out: Pick<FoodEntry, MicroKey> = {};
  if (typeof item.sf === "number") out.sf = round1(item.sf * scale);
  if (typeof item.na === "number") out.na = Math.round(item.na * scale);
  if (typeof item.ca === "number") out.ca = Math.round(item.ca * scale);
  if (typeof item.fe === "number") out.fe = round1(item.fe * scale);
  if (typeof item.pot === "number") out.pot = Math.round(item.pot * scale);
  return out;
}

export type MicroKey = "sf" | "na" | "ca" | "fe" | "pot";
export const MICRO_KEYS: MicroKey[] = ["sf", "na", "ca", "fe", "pot"];

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** The day's entries, tombstoned ones removed, oldest first. */
export function entriesFor(S: KayaState, ds: string): FoodEntry[] {
  const L = S.logs[ds];
  if (!L?.food?.length) return [];
  const off = L.foodOff || {};
  return L.food.filter((e) => !(e.id in off)).sort((a, b) => a.at - b.at);
}

export interface IntakeTotals {
  kcal: number;
  p: number;
  c: number;
  f: number;
  fb: number;
  sf: number;
  na: number;
  ca: number;
  fe: number;
  pot: number;
  items: number;
  /** how many of `items` carried micronutrients. Entries logged before the table had them add
   *  nothing to `sf`…`pot`, so a screen must say those totals are partial when this is short. */
  micro: number;
  /** how many of `items` carried each value a row may lack. Each sum above adds only the items
   *  that carry it, so where a count is short of `items` the sum is a lower bound, and where it
   *  is 0 there is no sum at all — "not known", never 0 (invariant 18). */
  known: Record<PartialKey, number>;
}

/** The values a food row or an entry may lack: fibre (a label need not declare it) and the five
 *  micronutrients. Energy, protein, carbohydrate and fat are always there. */
export type PartialKey = "fb" | MicroKey;
export const PARTIAL_KEYS: PartialKey[] = ["fb", ...MICRO_KEYS];

const emptyTotals = (): IntakeTotals => ({
  kcal: 0, p: 0, c: 0, f: 0, fb: 0, sf: 0, na: 0, ca: 0, fe: 0, pot: 0, items: 0, micro: 0,
  known: { fb: 0, sf: 0, na: 0, ca: 0, fe: 0, pot: 0 },
});

export function totalsOf(entries: FoodEntry[]): IntakeTotals {
  return entries.reduce<IntakeTotals>((a, e) => {
    const known = { ...a.known };
    for (const k of PARTIAL_KEYS) if (typeof e[k] === "number") known[k]++;
    return {
      kcal: a.kcal + e.kcal,
      p: round1(a.p + e.p),
      c: round1(a.c + e.c),
      f: round1(a.f + e.f),
      fb: round1(a.fb + (e.fb ?? 0)),
      sf: round1(a.sf + (e.sf ?? 0)),
      na: a.na + (e.na ?? 0),
      ca: a.ca + (e.ca ?? 0),
      fe: round1(a.fe + (e.fe ?? 0)),
      pot: a.pot + (e.pot ?? 0),
      items: a.items + 1,
      micro: a.micro + (MICRO_KEYS.some((k) => typeof e[k] === "number") ? 1 : 0),
      known,
    };
  }, emptyTotals());
}

/**
 * How far a sum can be trusted: `complete` when every item carried the value, `partial` when some
 * did — the sum is then a lower bound — and `none` when no item did, so there is no sum to show.
 * A day with nothing logged is `complete`: nothing eaten is a real zero.
 */
export function sumState(t: IntakeTotals, key: PartialKey): "complete" | "partial" | "none" {
  const k = t.known[key];
  if (k >= t.items) return "complete";
  return k > 0 ? "partial" : "none";
}

/** The entries that lack a value, in the order they were eaten — the foods a partial sum names. */
export function lacking(entries: FoodEntry[], key: PartialKey): FoodEntry[] {
  return entries.filter((e) => typeof e[key] !== "number");
}

/** A subtotal per meal slot, for the meal headers. */
export function mealTotals(entries: FoodEntry[]): Record<MealSlot, IntakeTotals> {
  const m = byMeal(entries);
  return {
    breakfast: totalsOf(m.breakfast),
    lunch: totalsOf(m.lunch),
    dinner: totalsOf(m.dinner),
    snack: totalsOf(m.snack),
  };
}

/** Share of energy from protein, carbohydrate and fat, in whole percent; null with nothing
 *  eaten. Worked from the macros (4/4/9) rather than the entries' kcal, so the three add to 100
 *  even where a source's energy figure includes fibre or alcohol. */
export function nutrientShare(t: Pick<IntakeTotals, "p" | "c" | "f">): {
  p: number;
  c: number;
  f: number;
} | null {
  const p = t.p * 4;
  const c = t.c * 4;
  const f = t.f * 9;
  const sum = p + c + f;
  if (!(sum > 0)) return null;
  const pp = Math.round((p / sum) * 100);
  const ff = Math.round((f / sum) * 100);
  return { p: pp, c: 100 - pp - ff, f: ff };
}

/**
 * The same meal, more or less of it — every portion multiplied by `factor`.
 *
 * Research item 9: a saved meal scaled by `remaining ÷ meal kcal` is the one primitive of
 * auto-planning worth having without photos or barcodes. Grams scale and the values are
 * recomputed from the table row through `entryFor` when `lookup` still finds it; otherwise the
 * copied values scale linearly, which is the same arithmetic on the numbers frozen at log time.
 * Ids and stamps are the caller's to mint — a scaled item is a new item (ADR-026).
 */
export function scaleItems(
  items: FoodEntry[],
  factor: number,
  lookup?: (code: string) => FoodItem | null
): FoodEntry[] {
  if (!Number.isFinite(factor) || factor <= 0) return [];
  return items.map((e) => {
    const qty = e.unit === "g" ? Math.round(e.qty * factor) : Math.round(e.qty * factor * 10) / 10;
    const oilTsp = e.oilTsp ? Math.round(e.oilTsp * factor * 2) / 2 : undefined;
    const item = e.code && lookup ? lookup(e.code) : null;
    if (item) {
      return entryFor(item, { qty, unit: e.unit, meal: e.meal, oilTsp, at: e.at, id: e.id });
    }
    const s = e.qty > 0 ? qty / e.qty : factor;
    const out: FoodEntry = {
      ...e,
      qty,
      grams: Math.round(e.grams * s),
      kcal: Math.round(e.kcal * s),
      p: round1(e.p * s),
      c: round1(e.c * s),
      f: round1(e.f * s),
    };
    if (typeof e.fb === "number") out.fb = round1(e.fb * s);
    if (oilTsp) out.oilTsp = oilTsp;
    else delete out.oilTsp;
    for (const k of MICRO_KEYS) {
      const v = e[k];
      if (typeof v === "number") out[k] = k === "sf" || k === "fe" ? round1(v * s) : Math.round(v * s);
    }
    return out;
  });
}

/** The factor that brings `items` to `kcal`, or null when there is nothing to scale. */
export function fitFactor(items: FoodEntry[], kcal: number): number | null {
  const have = items.reduce((a, e) => a + e.kcal, 0);
  if (!(have > 0) || !(kcal > 0)) return null;
  return kcal / have;
}

/* ---------- the plan (ADR-026) ---------- */

/** A day's planned items, removed ones dropped, oldest first. */
export function planFor(S: KayaState, ds: string): PlanItem[] {
  const day = S.mealPlan?.days?.[ds];
  if (!day?.items?.length) return [];
  const off = day.off || {};
  return day.items.filter((e) => !(e.id in off)).sort((a, b) => a.at - b.at);
}

/** The id a planned item is logged under. Derived, so logging it on two phones offline makes
 *  one entry, not two; a re-log after an un-log takes a numbered suffix — see `logPlanned`. */
export function loggedIdFor(planItemId: string): string {
  return `p:${planItemId}`;
}

/** Whether a planned item has been eaten — derived from the log, never stored. */
export function isLogged(S: KayaState, ds: string, planItemId: string): boolean {
  const base = loggedIdFor(planItemId);
  return entriesFor(S, ds).some((e) => e.id === base || e.id.startsWith(`${base}:`));
}

export function templatesOf(S: KayaState): MealTemplate[] {
  return Object.values(S.mealPlan?.templates || {}).sort((a, b) => a.name.localeCompare(b.name));
}

export function dayIntake(S: KayaState, ds: string): IntakeTotals {
  return totalsOf(entriesFor(S, ds));
}

export function byMeal(entries: FoodEntry[]): Record<MealSlot, FoodEntry[]> {
  const out: Record<MealSlot, FoodEntry[]> = { breakfast: [], lunch: [], dinner: [], snack: [] };
  for (const e of entries) out[e.meal]?.push(e);
  return out;
}

/** The deficit in force: the saved one, within what age and the answers allow (ADR-039); for a
 *  given day, none on a day marked as a fast (ADR-049). */
export function deficitTarget(S: KayaState, today = new Date(), device?: DeviceAnswers, ds?: string): number {
  return deficitRule(S, today, device, ds).kcal;
}

/** Grams of protein a day: the rate in force (ADR-039) × weight. */
export function proteinTarget(S: KayaState, weightKg = curWeight(S), today = new Date()): number {
  return Math.round(proteinRule(S, today).perKg * weightKg);
}

export interface EnergyBalance {
  /** eaten today */
  in: number;
  /** resting + incidental + the work Kaya measured */
  out: number;
  rest: number;
  baseline: number;
  active: number;
  /** what eating to plan would look like: out − the part of work not credited − deficit */
  budget: number;
  /** the part of `active` the budget does not credit (ADR-041) */
  heldBack: number;
  /** budget − in. Positive means there is room left. */
  left: number;
  deficit: number;
  /** true when the deficit asked for is more than a third of the day's burn */
  aggressive: boolean;
}

/**
 * The whole picture for a day, or null when the body numbers are not known.
 *
 * Null rather than a guess: without a year of birth and a sex there is no defensible resting
 * burn, and a calorie budget built on a placeholder is worse than none. The screens say what is
 * missing instead. (ADR-025, invariant 11)
 */
export function energyBalance(
  S: KayaState,
  ds: string,
  activeKcal: number,
  today = new Date(),
  device?: DeviceAnswers
): EnergyBalance | null {
  const burn = dailyBurn(S, curWeight(S), activeKcal, today);
  if (!burn) return null;

  const deficit = deficitTarget(S, today, device, ds);
  const eaten = dayIntake(S, ds).kcal;
  const budget = foodBudget(burn.total, burn.active, deficit);

  return {
    in: eaten,
    out: burn.total,
    rest: burn.rest,
    baseline: burn.baseline,
    active: burn.active,
    budget,
    heldBack: workHeldBack(burn.active),
    left: budget - eaten,
    deficit,
    aggressive: burn.total > 0 && deficit / burn.total > MAX_SAFE_DEFICIT_FRACTION,
  };
}

/**
 * Foods logged most often, newest first among equals — what a day's logging actually reaches for.
 *
 * Built from the log rather than a separate favourites list: the thing you eat every morning
 * should not also have to be curated by hand.
 */
export function frequentFoods(S: KayaState, limit = 12): FoodEntry[] {
  const seen = new Map<string, { e: FoodEntry; n: number }>();
  for (const L of Object.values(S.logs)) {
    for (const e of L.food || []) {
      if (L.foodOff && e.id in L.foodOff) continue;
      const key = e.code || e.name.toLowerCase();
      const cur = seen.get(key);
      if (!cur) seen.set(key, { e, n: 1 });
      else {
        cur.n++;
        if (e.at > cur.e.at) cur.e = e;
      }
    }
  }
  return [...seen.values()]
    .sort((a, b) => b.n - a.n || b.e.at - a.e.at)
    .slice(0, limit)
    .map((x) => x.e);
}
